import { useMemo, useState } from "react";
import { abreviar, escalaLineal, ticksBonitos } from "./escala";
import { Leyenda } from "./Figura";
import { useAncho } from "./useAncho";

export interface PuntoProyeccion { x: number; y: number | null; y2?: number | null }
export interface Marcador { x: number; tipo: "meta" | "cero" | "corte"; etiqueta: string }

/** Proyección: línea del patrimonio (2 px marino) + área de lo aportado (wash) + meta como hairline gris. Un solo eje Y. */
export function LineaProyeccion({ serie, meta, marcadores, xEtiqueta, formato, alto = 220, etiquetaY = "Patrimonio", etiquetaY2 = "Lo que aportas" }: {
  serie: PuntoProyeccion[]; meta: number | null; marcadores: Marcador[]; xEtiqueta: string; formato: (v: number) => string; alto?: number; etiquetaY?: string; etiquetaY2?: string;
}) {
  const { ref, ancho } = useAncho();
  const [hover, setHover] = useState<number | null>(null);
  const pad = { l: 44, r: 12, t: 16, b: 26 };
  const validos = serie.filter((p) => p.y !== null) as { x: number; y: number; y2?: number | null }[];
  const hayY2 = validos.some((p) => (p.y2 ?? 0) > 0);
  const { xs, ys, ticksY, ticksX } = useMemo(() => {
    const xMin = serie[0]?.x ?? 0, xMax = serie[serie.length - 1]?.x ?? 1;
    const yMax = Math.max(1, meta ?? 0, ...validos.map((p) => Math.max(p.y, p.y2 ?? 0)));
    const ticksY = ticksBonitos(0, yMax, 4);
    const xs = escalaLineal([xMin, xMax], [pad.l, ancho - pad.r]);
    const ys = escalaLineal([0, ticksY[ticksY.length - 1]], [alto - pad.b, pad.t]);
    const pasoX = Math.max(1, Math.ceil((xMax - xMin) / Math.max(2, Math.floor((ancho - pad.l) / 56))));
    const ticksX: number[] = [];
    for (let v = Math.ceil(xMin / pasoX) * pasoX; v <= xMax; v += pasoX) ticksX.push(v);
    return { xs, ys, ticksY, ticksX };
  }, [serie, meta, ancho, alto, validos]);
  if (validos.length === 0) return null;
  const linea = validos.map((p, i) => `${i ? "L" : "M"}${xs(p.x).toFixed(1)},${ys(p.y).toFixed(1)}`).join(" ");
  const area = hayY2 ? `M${xs(validos[0].x)},${ys(0)} ` + validos.map((p) => `L${xs(p.x).toFixed(1)},${ys(Math.max(0, p.y2 ?? 0)).toFixed(1)}`).join(" ") + ` L${xs(validos[validos.length - 1].x)},${ys(0)} Z` : null;
  const hp = hover === null ? null : validos[hover];
  const onMove = (clientX: number, rectLeft: number) => {
    const x = clientX - rectLeft;
    let best = 0, dist = Infinity;
    validos.forEach((p, i) => { const d = Math.abs(xs(p.x) - x); if (d < dist) { dist = d; best = i; } });
    setHover(best);
  };
  return (
    <div ref={ref} className="relative select-none">
      <svg width={ancho} height={alto} role="img" aria-label={`${etiquetaY} por ${xEtiqueta}`} className="block overflow-visible"
        onMouseMove={(e) => onMove(e.clientX, e.currentTarget.getBoundingClientRect().left)} onMouseLeave={() => setHover(null)}
        tabIndex={0} onKeyDown={(e) => { if (e.key === "ArrowRight") setHover((h) => Math.min(validos.length - 1, (h ?? -1) + 1)); if (e.key === "ArrowLeft") setHover((h) => Math.max(0, (h ?? 1) - 1)); }} onBlur={() => setHover(null)}>
        {ticksY.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={ancho - pad.r} y1={ys(t)} y2={ys(t)} stroke="var(--viz-grid)" strokeWidth={1} />
            <text x={pad.l - 6} y={ys(t)} dy="0.32em" textAnchor="end" className="tabular fill-[#6b7280] text-[10px]">{abreviar(t)}</text>
          </g>
        ))}
        {ticksX.map((t) => <text key={t} x={xs(t)} y={alto - 8} textAnchor="middle" className="tabular fill-[#6b7280] text-[10px]">{t}</text>)}
        {area && <path d={area} fill="var(--viz-area)" />}
        {meta !== null && meta > 0 && (
          <g>
            <line x1={pad.l} x2={ancho - pad.r} y1={ys(meta)} y2={ys(meta)} stroke="var(--viz-objetivo)" strokeWidth={1} />
            <text x={ancho - pad.r} y={ys(meta) - 5} textAnchor="end" className="fill-[#4b5563] text-[10px] font-medium">Meta {abreviar(meta)}</text>
          </g>
        )}
        {marcadores.filter((m) => m.tipo === "corte").map((m) => (
          <g key={`c${m.x}`}>
            <line x1={xs(m.x)} x2={xs(m.x)} y1={pad.t} y2={alto - pad.b} stroke="var(--viz-eje)" strokeWidth={1} />
            <text x={xs(m.x) > ancho * 0.7 ? xs(m.x) - 4 : xs(m.x) + 4} y={pad.t + 8} textAnchor={xs(m.x) > ancho * 0.7 ? "end" : "start"} className="fill-[#6b7280] text-[10px]">{m.etiqueta}</text>
          </g>
        ))}
        <path d={linea} fill="none" stroke="var(--viz-serie)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {marcadores.filter((m) => m.tipo !== "corte").map((m) => {
          const p = validos.find((v) => v.x === m.x);
          if (!p) return null;
          const color = m.tipo === "meta" ? "var(--viz-ok)" : "var(--viz-error)";
          return (
            <g key={`${m.tipo}${m.x}`}>
              <circle cx={xs(p.x)} cy={ys(p.y)} r={5} fill={color} stroke="#fff" strokeWidth={2} />
              <text x={xs(p.x)} y={ys(p.y) - 10} textAnchor="middle" className="text-[10px] font-semibold" style={{ fill: color }}>{m.etiqueta}</text>
            </g>
          );
        })}
        {hp && (
          <g>
            <line x1={xs(hp.x)} x2={xs(hp.x)} y1={pad.t} y2={alto - pad.b} stroke="var(--viz-eje)" strokeWidth={1} />
            <circle cx={xs(hp.x)} cy={ys(hp.y)} r={4.5} fill="var(--viz-serie)" stroke="#fff" strokeWidth={2} />
          </g>
        )}
      </svg>
      {hp && (
        <div className="viz-tooltip pointer-events-none absolute z-20 rounded-md border border-borde bg-white px-2.5 py-1.5 text-xs shadow-md"
          style={{ left: Math.min(ancho - 170, Math.max(0, xs(hp.x) - 80)), top: 0 }}>
          <div className="font-semibold">{xEtiqueta} {hp.x}</div>
          <div className="tabular">{etiquetaY}: {formato(hp.y)}</div>
          {hayY2 && <div className="tabular text-tinta-suave">{etiquetaY2}: {formato(Math.max(0, hp.y2 ?? 0))}</div>}
        </div>
      )}
      <Leyenda className="mt-1" items={[{ etiqueta: etiquetaY, color: "var(--viz-serie)", forma: "linea" }, ...(hayY2 ? [{ etiqueta: etiquetaY2, color: "rgba(61,65,82,.22)", forma: "rect" as const }] : []), ...(meta ? [{ etiqueta: "Meta", color: "var(--viz-objetivo)", forma: "linea" as const }] : [])]} />
    </div>
  );
}
